/**
 * TEMPLATE GALLERY - PROFESSIONAL PAYSTUB PREVIEWS
 * 
 * 6 UNIQUE TEMPLATES - Each visually distinct
 * Colors: Gray/White backgrounds, BLACK text for readability
 * Features: Preview button to see exact PDF before download
 */

import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, ChevronRight, Eye, X, ZoomIn, Download } from 'lucide-react';

// Sample data for previews
const SAMPLE_DATA = {
  company: {
    name: 'SAMPLE COMPANY INC',
    address: '123 Business Street',
    city: 'San Diego',
    state: 'CA',
    zip: '92101',
    phone: '(619) 555-1234'
  },
  employee: {
    name: 'JOHN DOE',
    address: '456 Employee Lane',
    city: 'San Diego',
    state: 'CA',
    zip: '92102',
    ssn: '1234'
  },
  pay: {
    checkNumber: '808267',
    periodStart: '02/01/2026',
    periodEnd: '02/14/2026',
    payDate: '02/19/2026',
    rate: 25.00,
    hours: 80,
    regular: 2000.00,
    grossPay: 2000.00,
    federalTax: 200.00,
    stateTax: 100.00,
    socialSecurity: 124.00,
    medicare: 29.00,
    sdi: 22.00,
    totalTaxes: 475.00,
    netPay: 1525.00,
    ytd: {
      regular: 6000.00,
      grossPay: 6000.00,
      federalTax: 600.00,
      stateTax: 300.00,
      socialSecurity: 372.00,
      medicare: 87.00,
      sdi: 66.00,
      totalTaxes: 1425.00,
      netPay: 4575.00
    }
  }
};

// 6 Template Definitions — original 3 plus the user-picked Carbonless,
// Federal, and Banking Statement.  Each template ships as a real PDF
// rendered via /api/paystub/sample-template/{id} so the gallery shows the
// actual document the user will get, not a fake CSS mockup.
export const PAYSTUB_TEMPLATES = [
  { id: 1, name: 'Executive Statement',    style: 'official_statement',    description: 'Navy & gold executive look with serif headline' },
  { id: 2, name: 'Modern Minimal',         style: 'modern_minimal',        description: 'Clean fintech style with teal accents and hero Net Pay' },
  { id: 3, name: 'Payroll Check Voucher',  style: 'traditional_voucher',   description: 'Authentic check stub with perforation & monospace amounts' },
  { id: 4, name: 'Aurora Modern',           style: 'aurora_modern',         description: 'Deep charcoal gradient hero with coral accent, big net-pay + stat chips, side-by-side rounded cards. Modern SaaS look.' },
  { id: 5, name: 'Pulse Minimal',           style: 'pulse_minimal',         description: 'Ultra-clean fintech dashboard export — indigo accent, generous whitespace, dark sidebar net-pay panel.' },
  { id: 6, name: 'Banking Statement',      style: 'banking_statement',     description: 'Navy header with sky-blue stat panel — feels like a bank deposit statement' },
];

// Template Preview Component — renders the REAL PDF via iframe so users
// see exactly what they'll get.  Backed by /api/paystub/sample-template/{id}.
const TemplatePreview = ({ template, isModal = false }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  // PNG render works reliably in every browser (including mobile/iOS/iframe
  // with no PDF plugin).  Same sample data as the PDF endpoint.
  const src = `${API}/api/paystub/sample-template-png/${template.id}`;
  return (
    <img
      key={template.id}
      src={src}
      alt={`Preview of ${template.name}`}
      className={`w-full h-full bg-white ${isModal ? "object-contain" : "object-cover object-top"}`}
      style={{ border: 0 }}
      loading="lazy"
    />
  );
};

// Legacy CSS-mockup body — retained in source for now in case we want to
// fall back to a tile-style render later.  Marked unused with leading
// underscore so the linter is happy.
const _LegacyTemplatePreview = ({ template, isModal = false }) => {
  const data = SAMPLE_DATA;
  const scale = isModal ? 'text-xs' : 'text-[7px]';
  const headerScale = isModal ? 'text-sm' : 'text-[9px]';
  const titleScale = isModal ? 'text-lg' : 'text-[11px]';
  
  // Common colors - Gray/White with BLACK text
  const grayBg = '#f3f4f6';      // Light gray background
  const darkGrayBg = '#6b7280';  // Dark gray for headers
  const mediumGray = '#9ca3af';  // Medium gray accent


  switch (template.style) {
    // TEMPLATE 1: Official Statement - Formal document style
    case 'official_statement':
      return (
        <div className={`bg-white ${scale} h-full flex flex-col border-2 border-gray-400`}>
          {/* Formal header */}
          <div className="p-2 border-b-2 border-gray-400 text-center">
            <div className={`font-bold ${titleScale} text-black tracking-wider`}>OFFICIAL PAYROLL STATEMENT</div>
            <div className="text-gray-600 text-[6px] mt-1">CONFIDENTIAL - FOR EMPLOYEE RECORDS ONLY</div>
          </div>
          
          {/* Document info */}
          <div className="p-2 grid grid-cols-2 border-b border-gray-300">
            <div>
              <div className="text-[6px] text-gray-600">EMPLOYER:</div>
              <div className="font-bold text-black">{data.company.name}</div>
              <div className="text-black">{data.company.address}</div>
              <div className="text-black">{data.company.city}, {data.company.state} {data.company.zip}</div>
            </div>
            <div className="text-right">
              <div className="text-[6px] text-gray-600">DOCUMENT DATE:</div>
              <div className="font-bold text-black">{data.pay.payDate}</div>
              <div className="text-[6px] text-gray-600 mt-1">REFERENCE NO:</div>
              <div className="text-black">{data.pay.checkNumber}</div>
            </div>
          </div>
          
          {/* Recipient */}
          <div className="p-2 border-b border-gray-300" style={{ backgroundColor: grayBg }}>
            <div className="text-[6px] text-gray-600">PAYABLE TO:</div>
            <div className="font-bold text-black">{data.employee.name}</div>
            <div className="text-black">SSN: XXX-XX-{data.employee.ssn}</div>
          </div>
          
          {/* Pay details in formal table */}
          <div className="flex-1 p-2">
            <table className="w-full border border-gray-400 text-black">
              <thead>
                <tr style={{ backgroundColor: grayBg }}>
                  <th className="border border-gray-400 p-1 text-left text-black">DESCRIPTION</th>
                  <th className="border border-gray-400 p-1 text-right text-black">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-400 p-1 text-black">Gross Earnings ({data.pay.hours} hours)</td>
                  <td className="border border-gray-400 p-1 text-right text-black">${data.pay.grossPay.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 p-1 text-black">Less: Federal Income Tax</td>
                  <td className="border border-gray-400 p-1 text-right text-black">({data.pay.federalTax})</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 p-1 text-black">Less: State Income Tax</td>
                  <td className="border border-gray-400 p-1 text-right text-black">({data.pay.stateTax})</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 p-1 text-black">Less: FICA (SS + Medicare)</td>
                  <td className="border border-gray-400 p-1 text-right text-black">({data.pay.socialSecurity + data.pay.medicare})</td>
                </tr>
                <tr style={{ backgroundColor: grayBg }}>
                  <td className="border border-gray-400 p-1 font-bold text-black">TOTAL DEDUCTIONS</td>
                  <td className="border border-gray-400 p-1 text-right font-bold text-black">({data.pay.totalTaxes})</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 p-1 font-bold text-black">NET AMOUNT PAYABLE</td>
                  <td className="border border-gray-400 p-1 text-right font-bold text-black">${data.pay.netPay.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Footer */}
          <div className="p-2 text-center border-t-2 border-gray-400 text-[6px] text-gray-600">
            This statement is computer generated and valid without signature
          </div>
        </div>
      );

    // TEMPLATE 2: Modern Minimal - Clean accent line style
    case 'modern_minimal':
      return (
        <div className={`bg-white ${scale} h-full flex flex-col`}>
          {/* Top accent line */}
          <div className="h-1" style={{ backgroundColor: darkGrayBg }}></div>
          
          {/* Header */}
          <div className="p-2 flex justify-between items-center">
            <div className={`font-bold ${titleScale} text-black`}>{data.company.name}</div>
            <div className="text-right">
              <div className={`font-bold ${titleScale} text-black`}>${data.pay.netPay.toLocaleString()}</div>
              <div className="text-gray-600 text-[6px]">NET PAY</div>
            </div>
          </div>
          
          {/* Info row */}
          <div className="px-2 py-1 flex justify-between text-[6px]" style={{ backgroundColor: grayBg }}>
            <span className="text-black">Employee: <span className="font-bold">{data.employee.name}</span></span>
            <span className="text-black">Period: {data.pay.periodStart} - {data.pay.periodEnd}</span>
            <span className="text-black">Pay Date: {data.pay.payDate}</span>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-2 space-y-2">
            {/* Earnings */}
            <div>
              <div className="text-[6px] uppercase tracking-wider text-gray-600 mb-1">Earnings</div>
              <div className="pl-2 border-l-2 border-gray-300">
                <div className="flex justify-between text-black">
                  <span>Regular ({data.pay.hours} hrs @ ${data.pay.rate})</span>
                  <span className="font-bold">${data.pay.regular.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-black font-bold mt-1 pt-1 border-t border-gray-200">
                  <span>Gross Pay</span>
                  <span>${data.pay.grossPay.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Taxes */}
            <div>
              <div className="text-[6px] uppercase tracking-wider text-gray-600 mb-1">Deductions</div>
              <div className="pl-2 border-l-2 border-gray-300 space-y-0.5">
                <div className="flex justify-between text-black"><span>Federal</span><span>${data.pay.federalTax}</span></div>
                <div className="flex justify-between text-black"><span>State</span><span>${data.pay.stateTax}</span></div>
                <div className="flex justify-between text-black"><span>Social Sec.</span><span>${data.pay.socialSecurity}</span></div>
                <div className="flex justify-between text-black"><span>Medicare</span><span>${data.pay.medicare}</span></div>
                <div className="flex justify-between text-black font-bold mt-1 pt-1 border-t border-gray-200">
                  <span>Total</span>
                  <span>${data.pay.totalTaxes}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom accent line */}
          <div className="h-1" style={{ backgroundColor: darkGrayBg }}></div>
        </div>
      );

    // TEMPLATE 3: Traditional Voucher - Classic green accounting style
    case 'traditional_voucher':
      return (
        <div className={`bg-white ${scale} h-full flex flex-col`}>
          {/* Classic green accounting header */}
          <div className="p-2 border-b-4 border-green-800" style={{ backgroundColor: '#f0f7f0' }}>
            <div className="flex justify-between items-center">
              <div>
                <div className={`font-bold ${titleScale} text-green-900`}>{data.company.name}</div>
                <div className="text-green-700 text-[7px]">{data.company.address}</div>
              </div>
              <div className="text-right">
                <div className="text-green-900 font-bold text-[8px]">PAYROLL CHECK</div>
                <div className="text-green-800 text-[7px]">#{data.pay.checkNumber}</div>
              </div>
            </div>
          </div>
          
          {/* Period banner */}
          <div className="bg-green-800 text-white text-center py-1 text-[7px]">
            Pay Period: {data.pay.periodStart} through {data.pay.periodEnd}
          </div>
          
          {/* Two-column layout */}
          <div className="flex flex-1">
            {/* Left: Employee & Pay Info */}
            <div className="w-1/2 p-2 border-r border-green-200">
              <div className="mb-2 p-1.5 rounded" style={{ backgroundColor: '#f8fdf8' }}>
                <div className="text-[6px] text-green-600 uppercase tracking-wider">Employee</div>
                <div className="font-bold text-black">{data.employee.name}</div>
                <div className="text-[7px] text-black">SSN: XXX-XX-{data.employee.ssn}</div>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between text-[7px] p-1 border-b border-green-100">
                  <span className="text-green-700">Hours Worked:</span>
                  <span className="font-bold text-black">{data.pay.hours}</span>
                </div>
                <div className="flex justify-between text-[7px] p-1 border-b border-green-100">
                  <span className="text-green-700">Rate:</span>
                  <span className="font-bold text-black">${data.pay.rate}/hr</span>
                </div>
                <div className="flex justify-between text-[7px] p-1 border-b border-green-100">
                  <span className="text-green-700">Gross Pay:</span>
                  <span className="font-bold text-black">${data.pay.grossPay.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Right: Deductions */}
            <div className="w-1/2 p-2">
              <div className="text-[6px] text-green-600 uppercase tracking-wider mb-1">Deductions</div>
              <div className="space-y-0.5 text-[7px]">
                <div className="flex justify-between"><span className="text-black">Federal Tax</span><span className="text-red-600">-${data.pay.federalTax}</span></div>
                <div className="flex justify-between"><span className="text-black">State Tax</span><span className="text-red-600">-${data.pay.stateTax}</span></div>
                <div className="flex justify-between"><span className="text-black">Social Security</span><span className="text-red-600">-${data.pay.socialSecurity}</span></div>
                <div className="flex justify-between"><span className="text-black">Medicare</span><span className="text-red-600">-${data.pay.medicare}</span></div>
                <div className="flex justify-between pt-1 mt-1 border-t border-green-200 font-bold">
                  <span className="text-black">Total Deductions</span>
                  <span className="text-red-700">-${data.pay.totalTaxes}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Net Pay Footer */}
          <div className="p-2 border-t-2 border-green-800" style={{ backgroundColor: '#e8f5e9' }}>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-[6px] text-green-600">PAY DATE</div>
                <div className="font-bold text-green-900">{data.pay.payDate}</div>
              </div>
              <div className="text-right">
                <div className="text-[6px] text-green-600">NET PAY</div>
                <div className={`font-bold ${titleScale} text-green-900`}>${data.pay.netPay.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      );

    default:
      return <div className="p-4 text-black">Template not found</div>;
  }
};
// Full-size Preview Modal
const PreviewModal = ({ template, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gray-100 p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-600">{template.description}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          <div className="aspect-[8.5/11] border border-gray-300 shadow-lg">
            <TemplatePreview template={template} isModal={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Template Gallery Component
export default function TemplateGallery({ selectedTemplate, onSelectTemplate, onNext }) {
  const [previewTemplate, setPreviewTemplate] = useState(null);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Choose Your Template</h2>
        <p className="text-zinc-400">Select a professional paystub design. Click &ldquo;Preview&rdquo; to see full details.</p>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {PAYSTUB_TEMPLATES.map(template => (
          <Card
            key={template.id}
            className={`cursor-pointer transition-all hover:scale-105 ${
              selectedTemplate === template.id
                ? 'ring-2 ring-green-500 bg-zinc-800'
                : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500'
            }`}
            onClick={() => onSelectTemplate(template.id)}
          >
            <CardContent className="p-3">
              {/* Template Preview */}
              <div className="aspect-[8.5/11] mb-3 bg-white rounded overflow-hidden border border-zinc-600">
                <TemplatePreview template={template} isModal={false} />
              </div>
              
              {/* Template Info */}
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-white text-sm">{template.name}</div>
                {selectedTemplate === template.id && (
                  <Badge className="bg-green-600 text-white">
                    <Check className="w-3 h-3 mr-1" />
                    Selected
                  </Badge>
                )}
              </div>
              
              {/* Preview Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewTemplate(template);
                }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Continue Button */}
      {selectedTemplate && (
        <div className="flex justify-end">
          <Button
            onClick={onNext}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Continue with {PAYSTUB_TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'Template'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        template={previewTemplate}
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
    </div>
  );
}
